import { Member, MemberUtils } from "@shared/member.utils";
import { PaymentUtils } from "@shared/payments.utils";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { SubscriptionCheckoutSummary } from "../../../supabase/functions/server/shared/types";
import { Button } from "../components/common/Button";
import {
  e2ePaymentEndpoint,
  invokeE2EPayment,
} from "../shared/e2e-payment";
import { getStripePromise } from "../shared/stripe";
import { supabaseClient } from "../shared/supabase";
import { useSeasonStore } from "../stores/season.store";
import type { MemberType } from "../stores/user.store";
import { useUserStore } from "../stores/user.store";
import * as SentryUtils from "../utils/sentry";

const CheckoutContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: white;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  margin: 20px;
`;

const Description = styled.p`
  margin: 20px;
`;

const MemberList = styled.div`
  border: 1px solid black;
  margin: 20px;
`;

const MemberItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const MemberName = styled.span`
  font-weight: bold;
`;

const MemberType = styled.span`
  color: #666;
`;

const Price = styled.span`
  font-weight: bold;
`;

const Total = styled.div`
  background: #e6f3ff;
  padding: 20px;
  margin: 20px;
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  font-size: 18px;
`;

const getMemberTypeLabel = (type: MemberType) => {
  switch (type) {
    case "adult":
      return "ADULTO - SENIOR";
    case "junior":
      return "FIGLIO - JUNIOR";
    case "baby":
      return "FIGLIO - BAMBINO";
    default:
      return type;
  }
};

export const Checkout = () => {
  const { profile, loadProfile } = useUserStore();
  const { currentSeason } = useSeasonStore();
  const [displayForm, setDisplayForm] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Refresh profile data when component mounts to ensure fresh subscription data
  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Extract selected members without valid subscription
  const selectedMembers = useMemo(() => {
    if (!currentSeason) {
      return [];
    }

    const memberIds: string[] = location.state?.memberIds || [];
    return (
      profile?.members
        .filter((member: { id: string }) =>
          memberIds.length !== 0 ? memberIds.includes(member.id) : true
        ) // is selected (or all if no selected)
        .filter(
          (member: Member) =>
            MemberUtils.getValidSubscription(member, currentSeason) === null
        ) || []
    ); // has invalid subscription
  }, [profile?.members, location.state?.memberIds, currentSeason]);

  const paymentDetails = useMemo(() => {
    if (!selectedMembers || !currentSeason) return null;
    return PaymentUtils.calculateSeasonPaymentDetails(
      selectedMembers,
      currentSeason
    );
  }, [selectedMembers, currentSeason]);

  if (!profile || !profile.members) {
    return null;
  }

  if (!currentSeason) {
    return (
      <CheckoutContainer>
        <PageTitle>PAGAMENTO ISCRIZIONE</PageTitle>
        <Description>
          I tesseramenti sono temporaneamente chiusi. Saranno riaperti con
          l'inizio della nuova stagione.
        </Description>
        <Button fullWidth onClick={() => navigate("/account")}>
          TORNA AL MIO ACCOUNT
        </Button>
      </CheckoutContainer>
    );
  }

  if (!paymentDetails) {
    return null;
  }

  const onCheckout = async () => {
    // Validate that we have members and a current season before proceeding
    if (!currentSeason || selectedMembers.length === 0) {
      console.warn("No valid members to checkout");
      return;
    }

    setIsLoading(true);

    const e2ePaymentEndpointEnabled = Boolean(e2ePaymentEndpoint);

    if (!e2ePaymentEndpointEnabled) {
      try {
        // Test Stripe loading before proceeding with checkout
        await getStripePromise();
      } catch (error) {
        SentryUtils.captureException(error, {
          tags: { component: "Checkout", operation: "stripe_loading" },
          extra: { selectedMembers: selectedMembers.length },
        });
        setIsLoading(false);
        return;
      }
    }

    const body: SubscriptionCheckoutSummary = {
      memberIDs: selectedMembers.map((member) => member.id),
    };

    let error;
    let data;
    try {
      if (e2ePaymentEndpointEnabled) {
        data = await invokeE2EPayment("/checkout-season", {
          method: "POST",
          body: JSON.stringify(body),
        });
      } else {
        ({ error, data } = await supabaseClient.functions.invoke(
          "server/checkout_season",
          { method: "POST", body },
        ));
      }
    } catch (checkoutError) {
      error = checkoutError;
    }

    if (error) {
      setIsLoading(false);
      SentryUtils.captureException(error, {
        tags: { component: "Checkout", operation: "checkout_session_creation" },
        extra: { selectedMembers },
      });
      console.error(error);
      return;
    }

    if (e2ePaymentEndpointEnabled && data.mockSessionId) {
      navigate(`/checkout-confirm?session_id=${data.mockSessionId}`);
      return;
    }

    const { clientSecret } = data;
    setClientSecret(clientSecret);
    setDisplayForm(true);
  };

  return (
    <CheckoutContainer>
      {clientSecret !== null && displayForm ? (
        <>
          <EmbeddedCheckoutProvider
            stripe={getStripePromise()}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </>
      ) : (
        <>
          <PageTitle>PAGAMENTO ISCRIZIONE</PageTitle>
          <Description>
            IN QUESTA PAGINA PUOI PAGARE LA QUOTA ASSOCIATIVA ANNUALE SINGOLA E
            DELLA INTERA FAMIGLIA
          </Description>
          <MemberList>
            {paymentDetails.breakdown.map((item) => (
              <MemberItem key={item.memberId}>
                <MemberInfo>
                  <MemberName>
                    {item.firstName} {item.lastName}
                  </MemberName>
                  <MemberType>{getMemberTypeLabel(item.memberType)}</MemberType>
                  <div>TESSERA {currentSeason.name}</div>
                </MemberInfo>
                <Price>{item.amount.toFixed(2)} €</Price>
              </MemberItem>
            ))}
          </MemberList>
          <Total>
            <span>TOTALE</span>
            <span>{paymentDetails.total.toFixed(2)} €</span>
          </Total>
          <Button
            fullWidth
            onClick={onCheckout}
            loading={isLoading}
            loadingText="PAGAMENTO IN CORSO..."
          >
            PROCEDI CON IL PAGAMENTO
          </Button>
        </>
      )}
    </CheckoutContainer>
  );
};
